import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { GoogleMapsLoaderService } from '../services/google-maps-loader.service';

interface MapCenter {
  lat: number;
  lng: number;
}

type MarkerType = google.maps.marker.AdvancedMarkerElement | google.maps.Marker;

@Component({
  selector: 'app-google-map',
  standalone: true,
  imports: [CommonModule, GoogleMapsModule],
  template: `
    <div class="map-container" *ngIf="isGoogleMapsLoaded && hasValidLocation">
      <google-map
        #mapElement
        [height]="height"
        [width]="width"
        [center]="center!"
        [zoom]="zoom"
        [options]="mapOptions"
        class="google-map"
      >
      </google-map>
    </div>

    <div class="map-loading" *ngIf="isLoading">
      <div class="loading-spinner"></div>
      <p class="loading-text">Loading map...</p>
    </div>

    <div class="map-error" *ngIf="apiError">
      <div class="error-icon">⚠️</div>
      <p class="error-title">Map Service Unavailable</p>
      <p class="error-message">
        We're having trouble loading the map. Please try again later.
      </p>
    </div>

    <div
      class="map-location-error"
      *ngIf="!isLoading && !apiError && locationError && searchedLocation"
    >
      <div class="location-error-icon">📍</div>
      <p class="location-error-title">Location Not Found</p>
      <p class="location-error-message">
        We couldn't find "<strong>{{ searchedLocation }}</strong
        >" on the map.
      </p>
      <p class="location-error-hint">
        The location might be misspelled or doesn't exist in our database.
      </p>
    </div>

    <div
      class="map-no-coordinates"
      *ngIf="
        !isLoading &&
        !apiError &&
        !locationError &&
        !hasValidLocation &&
        isGoogleMapsLoaded
      "
    >
      <div class="no-location-icon">🗺️</div>
      <p class="no-location-text">No location specified</p>
    </div>
  `,
  styleUrls: ['./google-map.component.css'],
})
export class GoogleMapComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @Input() cityName: string = '';
  @Input() locationName: string = '';
  @Input() height: string = '300px';
  @Input() width: string = '100%';
  @Input() zoom: number = 10;

  @ViewChild('mapElement', { static: false }) mapElement?: GoogleMap;

  center: MapCenter | null = null;
  isGoogleMapsLoaded = false;
  isLoading = true;
  apiError = false;
  locationError = false;
  searchedLocation = '';
  hasValidLocation = false;

  private marker: MarkerType | null = null;
  private geocoder: google.maps.Geocoder | null = null;
  private useAdvancedMarkers = true;
  private markerCreationTimeout: any = null;
  private pendingMarkerCreation = false;

  mapOptions: google.maps.MapOptions = {
    mapId: '62c67aaf703c32505ac4ab37',
    disableDefaultUI: false,
    clickableIcons: false,
    disableDoubleClickZoom: false,
    scrollwheel: true,
    gestureHandling: 'cooperative',
  };

  constructor(private googleMapsLoader: GoogleMapsLoaderService) {}

  ngOnInit(): void {
    this.loadGoogleMaps();
  }

  ngAfterViewInit(): void {
    if (this.pendingMarkerCreation && this.center) {
      this.createMarker();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cityName'] || changes['locationName']) {
      this.removeMarker();
      this.clearState();
      if (this.isGoogleMapsLoaded) {
        this.geocodeLocation();
      }
    }
  }

  ngOnDestroy(): void {
    this.clearMarkerTimeout();
    this.removeMarker();
  }

  private loadGoogleMaps(): void {
    this.isLoading = true;
    this.apiError = false;
    this.locationError = false;

    this.googleMapsLoader
      .loadGoogleMaps()
      .then(() => {
        this.isGoogleMapsLoaded = true;
        this.isLoading = false;

        if (!this.isGoogleMapsApiValid()) {
          this.apiError = true;
          return;
        }

        this.initializeGeocoder();
        this.checkAdvancedMarkerSupport();
        this.geocodeLocation();
      })
      .catch(() => {
        this.isLoading = false;
        this.apiError = true;
      });
  }

  private isGoogleMapsApiValid(): boolean {
    return (
      typeof google !== 'undefined' && !!google.maps && !!google.maps.Geocoder
    );
  }

  private initializeGeocoder(): void {
    if (this.isGoogleMapsApiValid()) {
      this.geocoder = new google.maps.Geocoder();
    }
  }

  private checkAdvancedMarkerSupport(): void {
    this.useAdvancedMarkers = !!google.maps?.marker?.AdvancedMarkerElement;
  }

  private geocodeLocation(): void {
    if (!this.isGoogleMapsLoaded || !this.geocoder) {
      return;
    }

    const locationQuery = (this.cityName || this.locationName).trim();

    if (!locationQuery) {
      this.clearState();
      return;
    }

    this.searchedLocation = locationQuery;
    this.locationError = false;

    this.geocoder.geocode({ address: locationQuery }, (results, status) => {
      this.handleGeocodeResponse(results, status);
    });
  }

  private handleGeocodeResponse(
    results: google.maps.GeocoderResult[] | null,
    status: google.maps.GeocoderStatus
  ): void {
    if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
      const location = results[0].geometry.location;
      this.center = {
        lat: location.lat(),
        lng: location.lng(),
      };
      this.hasValidLocation = true;
      this.locationError = false;

      this.pendingMarkerCreation = true;
      setTimeout(() => {
        this.createMarker();
      }, 100);
    } else {
      this.clearState();
      this.locationError = true;
    }
  }

  private createMarker(): void {
    this.clearMarkerTimeout();
    this.removeMarker();
    this.pendingMarkerCreation = false;

    if (!this.mapElement?.googleMap || !this.center) {
      if (this.center) {
        this.pendingMarkerCreation = true;
      }
      return;
    }

    this.markerCreationTimeout = setTimeout(() => {
      if (this.useAdvancedMarkers) {
        this.createAdvancedMarker();
      } else {
        this.createStandardMarker();
      }
    }, 500);
  }

  private createAdvancedMarker(): void {
    if (!this.mapElement?.googleMap || !this.center) {
      return;
    }

    if (!google.maps?.marker?.AdvancedMarkerElement) {
      this.useAdvancedMarkers = false;
      this.createStandardMarker();
      return;
    }

    try {
      const pinElement = new google.maps.marker.PinElement({
        background: '#EA4335',
        borderColor: '#C5221F',
        glyphColor: '#FFFFFF',
        scale: 1.2,
      });

      this.marker = new google.maps.marker.AdvancedMarkerElement({
        map: this.mapElement.googleMap,
        position: this.center,
        title: this.searchedLocation || 'Location',
        content: pinElement.element,
      });
    } catch {
      this.useAdvancedMarkers = false;
      this.createStandardMarker();
    }
  }

  private createStandardMarker(): void {
    if (!this.mapElement?.googleMap || !this.center) {
      return;
    }

    try {
      this.marker = new google.maps.Marker({
        map: this.mapElement.googleMap,
        position: this.center,
        title: this.searchedLocation || 'Location',
        icon: {
          url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
          scaledSize: new google.maps.Size(40, 40),
        },
        animation: google.maps.Animation.DROP,
      });
    } catch {}
  }

  private removeMarker(): void {
    if (!this.marker) {
      return;
    }

    try {
      if (this.marker instanceof google.maps.marker.AdvancedMarkerElement) {
        this.marker.map = null;
      } else if (this.marker instanceof google.maps.Marker) {
        this.marker.setMap(null);
      }
    } finally {
      this.marker = null;
    }
  }

  private clearMarkerTimeout(): void {
    if (this.markerCreationTimeout) {
      clearTimeout(this.markerCreationTimeout);
      this.markerCreationTimeout = null;
    }
  }

  private clearState(): void {
    this.center = null;
    this.hasValidLocation = false;
    this.removeMarker();
  }
}
